// Mini modal das injeções de UMA fatura, aberto pelas colunas de injeção do Resumo.
//
// O Resumo mostra o SOMATÓRIO por origem; quando a fatura tem mais de uma parcela, não
// dá para editar o total direto (o valor não teria em qual coluna cair). Aqui a conta é
// aberta linha a linha: cada parcela com a sua UC geradora e os seus kWh, e o que se
// muda vai para a coluna correspondente da aba Faturas — que continua sendo a fonte.
//
// Editar a UC geradora muda a ORIGEM da parcela (a badge e a barra acompanham enquanto
// se digita): é assim que uma injeção que caiu em "sem UC" ou em "Outros" por erro de
// extração passa a contar como Vigor.

import React, { useRef, useState } from 'react';
import { X, Info, Zap } from 'lucide-react';
import type { OrigemInjecao, ParcelaInjecao } from './faturaGridModel';
import { origemDaUc } from './faturaGridModel';
import { editableText, formatCell, parseCell } from './gridFormat';

interface Props {
  titulo: string;
  parcelas: ParcelaInjecao[];
  ucsVigor: Set<string> | null;
  editavel: boolean;
  onClose: () => void;
  /** Células da aba Faturas a gravar (coluna → valor cru), na linha desta fatura. */
  onAplicar: (mudancas: { col: number; valor: string | number }[]) => void;
}

const ORIGENS: OrigemInjecao[] = ['vigor', 'outros', 'semUc'];
const ROTULO: Record<OrigemInjecao, string> = { vigor: 'Vigor', outros: 'Outros', semUc: 'Sem UC' };
const BADGE: Record<OrigemInjecao, string> = {
  vigor: 'bg-primary/15 text-primary dark:bg-primary/25 dark:text-[#C3D18C]',
  outros: 'bg-secondary/20 text-amber-800 dark:bg-secondary/25 dark:text-amber-200',
  semUc: 'bg-gray-200 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};
const BARRA: Record<OrigemInjecao, string> = { vigor: 'bg-primary', outros: 'bg-secondary', semUc: 'bg-gray-400 dark:bg-gray-600' };

const InjecoesFaturaModal: React.FC<Props> = ({ titulo, parcelas, ucsVigor, editavel, onClose, onAplicar }) => {
  const [rascunho, setRascunho] = useState(() => parcelas.map(p => ({ uc: p.uc, qtd: editableText(p.qtd, 'num') })));
  // Só fecha no clique de fundo quando o gesto COMEÇOU no fundo: selecionar o texto de um
  // campo e soltar o mouse fora não pode jogar fora o que foi digitado.
  const gestoNoFundo = useRef(false);

  // A origem de cada linha vem do que está DIGITADO — quem troca a UC vê a badge virar
  // antes de aplicar, que é o ponto de abrir esta janela.
  const linhas = parcelas.map((p, i) => {
    const d = rascunho[i];
    const qtd = Number(parseCell(d.qtd, 'num')) || 0;
    return { p, d, qtd, origem: p.colUc === null ? p.origem : origemDaUc(d.uc, ucsVigor) };
  });

  const totais: Record<OrigemInjecao, number> = { vigor: 0, outros: 0, semUc: 0 };
  for (const l of linhas) if (l.p.contaNoTotal) totais[l.origem] += l.qtd;
  const total = totais.vigor + totais.outros + totais.semUc;

  const mudancas = linhas.flatMap(({ p, d }) => {
    const out: { col: number; valor: string | number }[] = [];
    if (d.qtd !== editableText(p.qtd, 'num')) out.push({ col: p.colQtd, valor: parseCell(d.qtd, 'num') });
    if (p.colUc !== null && d.uc.trim() !== p.uc) out.push({ col: p.colUc, valor: d.uc.trim() });
    return out;
  });

  const set = (i: number, campo: 'uc' | 'qtd', valor: string) =>
    setRascunho(r => r.map((x, j) => (j === i ? { ...x, [campo]: valor } : x)));

  const input = 'w-full bg-white dark:bg-gray-950 border border-gray-200 dark:border-gray-700 rounded-lg px-2 py-1 outline-none transition-shadow focus:ring-2 focus:ring-primary/60 focus:border-primary disabled:bg-transparent disabled:border-transparent disabled:px-0 disabled:cursor-default';

  return (
    <div
      className="absolute inset-0 z-20 flex items-center justify-center p-4 bg-black/40 backdrop-blur-[2px] animate-fade-in"
      onMouseDown={e => { gestoNoFundo.current = e.target === e.currentTarget; }}
      onClick={() => { if (gestoNoFundo.current) onClose(); gestoNoFundo.current = false; }}
    >
      <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-2xl max-h-full flex flex-col overflow-hidden" onClick={e => e.stopPropagation()}>
        {/* Cabeçalho: quem é a fatura + quanto injetou no total */}
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800 flex items-start justify-between gap-3">
          <div className="flex items-start gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-xl bg-primary/15 dark:bg-primary/25 flex items-center justify-center shrink-0">
              <Zap className="w-4 h-4 text-primary dark:text-[#C3D18C]" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-bold text-gray-800 dark:text-gray-100">
                Injeções da fatura <span className="text-gray-400 font-semibold">· {parcelas.length} parcela(s)</span>
              </h3>
              <p className="text-[12px] text-gray-500 dark:text-gray-400 truncate">{titulo}</p>
            </div>
          </div>
          <button onClick={onClose} title="Fechar" className="w-7 h-7 rounded-lg flex items-center justify-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-auto custom-scrollbar px-4 py-3">
          <table className="w-full text-[12px] border-separate border-spacing-y-1">
            <thead>
              <tr className="text-[10px] uppercase tracking-wide text-gray-400 dark:text-gray-500 text-left">
                <th className="font-bold px-2 pb-1">Origem</th>
                <th className="font-bold px-2 pb-1">UC geradora</th>
                <th className="font-bold px-2 pb-1 text-right">kWh</th>
                <th className="font-bold px-2 pb-1">Coluna na aba Faturas</th>
              </tr>
            </thead>
            <tbody>
              {linhas.map(({ p, d, origem }, i) => (
                <tr key={p.colQtd} className={`${p.contaNoTotal ? '' : 'opacity-60'} bg-gray-50/70 dark:bg-gray-800/40`}>
                  <td className="px-2 py-1.5 align-middle rounded-l-lg">
                    <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide ${BADGE[origem]}`}>
                      {ROTULO[origem]}
                    </span>
                  </td>
                  <td className="px-2 py-1.5 align-middle">
                    {p.colUc !== null ? (
                      <input
                        value={d.uc}
                        disabled={!editavel}
                        onChange={e => set(i, 'uc', e.target.value)}
                        placeholder="não veio na fatura"
                        className={`${input} font-mono`}
                      />
                    ) : (
                      <span className="text-gray-400 dark:text-gray-500 italic" title="O layout do Grupo A / tarifa branca não traz a UC geradora por parcela.">
                        o layout não traz a UC
                      </span>
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-middle w-32">
                    <input
                      value={d.qtd}
                      disabled={!editavel}
                      onChange={e => set(i, 'qtd', e.target.value)}
                      className={`${input} text-right font-mono tabular-nums`}
                    />
                  </td>
                  <td className="px-2 py-1.5 align-middle text-gray-500 dark:text-gray-400 rounded-r-lg">
                    {p.rotulo}
                    {!p.contaNoTotal && <span className="block text-[10px] text-gray-400">não soma — mesma energia do TUSD</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Proporção do que entrou por origem — é o que o Resumo vai mostrar. */}
          <div className="mt-4">
            <div className="flex h-2 rounded-full overflow-hidden bg-gray-100 dark:bg-gray-800">
              {ORIGENS.map(o => (
                totais[o] > 0 && <div key={o} className={BARRA[o]} style={{ width: `${(totais[o] / total) * 100}%` }} title={`${ROTULO[o]}: ${formatCell(totais[o], 'num')} kWh`} />
              ))}
            </div>
            <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px]">
              {ORIGENS.map(o => (
                <span key={o} className="inline-flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
                  <i className={`w-2 h-2 rounded-full ${BARRA[o]}`} />
                  {ROTULO[o]}
                  <b className="font-mono tabular-nums text-gray-800 dark:text-gray-100">{formatCell(totais[o], 'num')}</b>
                </span>
              ))}
              <span className="ml-auto text-gray-400">
                total <b className="font-mono tabular-nums text-gray-600 dark:text-gray-300">{formatCell(total, 'num')}</b> kWh
              </span>
            </div>
          </div>

          {editavel && (
            <p className="mt-3 flex items-start gap-1.5 text-[11px] text-gray-500 dark:text-gray-400">
              <Info className="w-3.5 h-3.5 shrink-0 mt-px" />
              Cada campo aqui é uma célula da aba Faturas. Trocar a UC geradora muda de quem é a energia; os kWh entram no total daquela origem. Depois ainda é preciso “Salvar alterações” para gravar no banco.
            </p>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-800 flex items-center justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-[12px] font-medium rounded-lg border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">
            {editavel ? 'Cancelar' : 'Fechar'}
          </button>
          {editavel && (
            <button
              onClick={() => onAplicar(mudancas)}
              disabled={mudancas.length === 0}
              className="px-3 py-1.5 text-[12px] font-semibold rounded-lg bg-green-600 hover:bg-green-700 text-white disabled:opacity-50"
            >
              Aplicar{mudancas.length > 0 ? ` (${mudancas.length})` : ''}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default InjecoesFaturaModal;
